import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowLeft, GraduationCap, School, MapPin, BookOpen,
  CheckCircle, Loader2, Users, Trophy, Target, Clock, Mail, Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TeacherRegisterPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [formData, setFormData] = useState({
    school_name: "",
    school_district: "",
    state: "",
    grade_levels: [],
    disciplines: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check if user already has a teacher registration
  useEffect(() => {
    const checkRegistration = async () => {
      if (!user) {
        setCheckingStatus(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/teachers/me`, { withCredentials: true });
        if (response.data) {
          setExistingRegistration(response.data);
        }
      } catch (error) {
        // No existing registration - that's fine
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkRegistration();
  }, [user]);

  const gradeLevelOptions = ["K-2", "3-5", "6-8", "9-12", "College", "Administrator"];
  const disciplineOptions = [
    "Agriculture",
    "Science",
    "Biology",
    "Environmental Science",
    "Math",
    "STEM",
    "CTE",
    "Art",
    "Home Economics",
    "Special Education",
    "Music",
    "Cultural Education",
    "Other"
  ];

  const handleSubmit = async () => {
    if (!user) {
      toast.error("Please log in first");
      login();
      return;
    }

    if (!formData.school_name) {
      toast.error("Please enter your school name");
      return;
    }

    setSubmitting(true);
    try {
      // Send disciplines as subjects for backend compatibility
      const payload = {
        ...formData,
        subjects: formData.disciplines
      };
      await axios.post(`${API}/teachers/register`, payload, { withCredentials: true });
      toast.success("Teacher registration submitted!");
      navigate("/challenges");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleGradeLevel = (level) => {
    setFormData(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(level)
        ? prev.grade_levels.filter(l => l !== level)
        : [...prev.grade_levels, level]
    }));
  };

  const toggleDiscipline = (discipline) => {
    setFormData(prev => ({
      ...prev,
      disciplines: prev.disciplines.includes(discipline)
        ? prev.disciplines.filter(d => d !== discipline)
        : [...prev.disciplines, discipline]
    }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-blue-500" />
            <h2 className="text-xl font-bold mb-2">Sign In Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to register as a teacher or check your registration status.
            </p>
            <Button onClick={login} size="lg" data-testid="teacher-login-btn">
              Sign In with Google
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Show existing registration status
  if (existingRegistration) {
    const isVerified = existingRegistration.verification_status === 'verified';
    const isPending = existingRegistration.verification_status === 'pending';
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/challenges")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Challenges
            </Button>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <Card>
            <CardContent className="p-8">
              {/* Status Header */}
              <div className="text-center mb-8">
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  isVerified ? 'bg-green-100' : 'bg-amber-100'
                }`}>
                  {isVerified ? (
                    <Shield className="w-10 h-10 text-green-600" />
                  ) : (
                    <Clock className="w-10 h-10 text-amber-600" />
                  )}
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  {isVerified ? 'Teacher Verified!' : 'Registration Pending'}
                </h2>
                <p className={`text-sm ${isVerified ? 'text-green-600' : 'text-amber-600'}`}>
                  {isVerified 
                    ? 'You have full access to create and manage challenges' 
                    : 'Your registration is being reviewed by our team'}
                </p>
              </div>

              {/* Registration Details */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="font-semibold text-lg mb-4">Your Registration Details</h3>
                
                <div className="grid gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Mail className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium">{existingRegistration.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <School className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-xs text-slate-500">School</p>
                      <p className="font-medium">{existingRegistration.school_name || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  {existingRegistration.school_district && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">District</p>
                        <p className="font-medium">{existingRegistration.school_district}</p>
                      </div>
                    </div>
                  )}
                  
                  {existingRegistration.state && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500">State/Region</p>
                        <p className="font-medium">{existingRegistration.state}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Grade Levels */}
                {existingRegistration.grade_levels?.length > 0 && (
                  <div className="pt-4">
                    <p className="text-sm text-slate-500 mb-2">Grade Levels</p>
                    <div className="flex flex-wrap gap-2">
                      {existingRegistration.grade_levels.map((level, i) => (
                        <span key={i} className={`px-3 py-1 rounded-full text-sm ${
                          level === 'Administrator' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {level}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Disciplines */}
                {existingRegistration.subjects?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm text-slate-500 mb-2">Disciplines</p>
                    <div className="flex flex-wrap gap-2">
                      {existingRegistration.subjects.map((subj, i) => (
                        <span key={i} className={`px-3 py-1 rounded-full text-sm ${
                          ['Special Education', 'Music', 'Cultural Education'].includes(subj)
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          {subj}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="border-t mt-6 pt-6 space-y-3">
                <Button 
                  onClick={() => navigate("/challenges")} 
                  className="w-full"
                  size="lg"
                  data-testid="go-to-challenges-btn"
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  {isVerified ? 'Create a Challenge' : 'View Challenges'}
                </Button>
                
                {!isVerified && (
                  <p className="text-xs text-center text-muted-foreground">
                    Verification typically takes 1-2 business days. You&apos;ll receive an email when verified.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/challenges")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-500" />
              Teacher Registration
            </h1>
            <p className="text-sm text-muted-foreground">Create and manage design challenges</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Benefits */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-4">Why Become a ROOTSPACE Teacher?</h2>
              <p className="text-muted-foreground">
                Create engaging garden design challenges that teach real-world agricultural skills and STEM concepts.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  icon: Trophy,
                  title: "Create Custom Challenges",
                  description: "Design themed challenges with specific goals, requirements, and learning objectives."
                },
                {
                  icon: Target,
                  title: "Set Educational Standards",
                  description: "Align challenges with NGSS, CTE, and Common Core Math standards."
                },
                {
                  icon: Users,
                  title: "Engage Students",
                  description: "Students compete, vote, and learn about food security, biodiversity, and sustainability."
                },
                {
                  icon: BookOpen,
                  title: "Track Learning",
                  description: "View submissions, provide feedback, and assess student understanding."
                }
              ].map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Registration Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <School className="w-5 h-5" />
                Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>School Name *</Label>
                <Input
                  value={formData.school_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, school_name: e.target.value }))}
                  placeholder="Enter your school name"
                  data-testid="school-name-input"
                />
              </div>

              <div>
                <Label>School District</Label>
                <Input
                  value={formData.school_district}
                  onChange={(e) => setFormData(prev => ({ ...prev, school_district: e.target.value }))}
                  placeholder="Enter your school district"
                />
              </div>

              <div>
                <Label>State/Region</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="Enter your state or region"
                />
              </div>

              <div>
                <Label className="mb-3 block">Grade Levels You Teach</Label>
                <div className="flex flex-wrap gap-2">
                  {gradeLevelOptions.map(level => (
                    <Button
                      key={level}
                      type="button"
                      variant={formData.grade_levels.includes(level) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleGradeLevel(level)}
                      data-testid={`grade-level-${level.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {formData.grade_levels.includes(level) && <CheckCircle className="w-4 h-4 mr-1" />}
                      {level}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Disciplines</Label>
                <div className="flex flex-wrap gap-2">
                  {disciplineOptions.map(discipline => (
                    <Button
                      key={discipline}
                      type="button"
                      variant={formData.disciplines.includes(discipline) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleDiscipline(discipline)}
                      data-testid={`discipline-${discipline.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {formData.disciplines.includes(discipline) && <CheckCircle className="w-4 h-4 mr-1" />}
                      {discipline}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full"
                  size="lg"
                  data-testid="register-teacher-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <GraduationCap className="w-4 h-4 mr-2" />
                  )}
                  Register as Teacher
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  You&apos;ll be verified instantly and can start creating challenges right away!
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TeacherRegisterPage;
